import { useMemo, useState, type FormEvent } from 'react';
import {
  BMC_BLOCKS,
  BUSINESS_PLAN_FIELDS,
  MAX_PROJECTS_PER_YEAR,
  emptyBusinessPlanContent,
  entryKindMeta,
  IDEATION_ENTRY_KINDS,
  ideationStatusLabel,
  type GenerateIdeasResponse,
  type Ideation,
  type IdeationEntryKind,
  type SuggestedIdea,
} from '@isbd/shared';
import { invokeAiFunction, supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { PpcoPanel } from '@/features/ppco/PpcoPanel';
import { ScamperPanel } from '@/features/scamper/ScamperPanel';
import { FeasibilityWizard } from '@/features/feasibility/FeasibilityWizard';
import { DiagnosticWizard } from '@/features/diagnostic/DiagnosticWizard';
import { ConceptionProcessSteps } from '@/features/conception/ConceptionProcessSteps';
import { ConceptionSynthesis } from '@/features/conception/ConceptionSynthesis';
import { useToast } from '@/features/ui/toast/useToast';
import '@/features/conception/conception-process.css';

type Step = 1 | 2 | 3;
type ModelingDoc = 'choice' | 'bmc' | 'bp';

const emptyBmc = Object.fromEntries(BMC_BLOCKS.map((b) => [b.key, ''])) as Record<string, string>;

const STEP_LABELS: Record<Step, string> = {
  1: 'Idéation',
  2: 'Diagnostic / Faisabilité',
  3: 'Modélisation',
};

const ENTRY_CARD_META: Record<
  IdeationEntryKind,
  { tone: 'blue' | 'orange' | 'green'; step: string; icon: string; cta: string }
> = {
  assisted: { tone: 'blue', step: '01', icon: 'IA', cta: 'Générer des idées' },
  new_idea: { tone: 'orange', step: '02', icon: 'ID', cta: 'Saisir mon idée' },
  existing_activity: { tone: 'green', step: '03', icon: 'EX', cta: 'Continuer' },
};

export function ConceptionPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [ideationId, setIdeationId] = useState<string | null>(null);
  const [entryKind, setEntryKind] = useState<IdeationEntryKind | null>(null);
  const [ppcoDone, setPpcoDone] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [ideationModalOpen, setIdeationModalOpen] = useState(false);
  const [step2Done, setStep2Done] = useState(false);
  const [modelingDoc, setModelingDoc] = useState<ModelingDoc>('choice');

  const [contextForm, setContextForm] = useState({
    domain: '',
    country_code: 'CI',
    city: '',
    target_audience: '',
    investment_capital: '',
  });
  const [manualIdea, setManualIdea] = useState({
    business_idea: '',
    problems: '',
    solutions: '',
    activity_notes: '',
  });
  /** Suggestions IA non persistées tant qu’aucune n’est choisie. */
  const [suggestions, setSuggestions] = useState<SuggestedIdea[]>([]);
  const [generated, setGenerated] = useState<SuggestedIdea | null>(null);
  const [currentIdeaTitle, setCurrentIdeaTitle] = useState('');
  const [bmc, setBmc] = useState(emptyBmc);
  const [bpContent, setBpContent] = useState(() => emptyBusinessPlanContent());

  const requiresPpco = entryKind ? entryKindMeta(entryKind).requiresPpco : true;
  const isExisting = entryKind === 'existing_activity';
  const step2Label = isExisting ? 'Diagnostic IGPE' : 'Faisabilité';
  const entryMeta = entryKind ? entryKindMeta(entryKind) : null;

  const bpSections = useMemo(() => {
    const map = new Map<string, { key: string; label: string; section: string }[]>();
    for (const f of BUSINESS_PLAN_FIELDS) {
      const list = map.get(f.section) ?? [];
      list.push({ key: f.key, label: f.label, section: f.section });
      map.set(f.section, list);
    }
    return [...map.entries()];
  }, []);

  function resetIdeaWork() {
    setIdeationId(null);
    setGenerated(null);
    setSuggestions([]);
    setCurrentIdeaTitle('');
    setPpcoDone(false);
    setManualIdea({ business_idea: '', problems: '', solutions: '', activity_notes: '' });
    setBmc(emptyBmc);
    setBpContent(emptyBusinessPlanContent());
    setStep2Done(false);
    setModelingDoc('choice');
  }

  function chooseEntryKind(kind: IdeationEntryKind) {
    setEntryKind(kind);
    resetIdeaWork();
    setStep(1);
    setIdeationModalOpen(true);
  }

  function closeIdeationModal() {
    setIdeationModalOpen(false);
  }

  function goToStep(next: Step) {
    setStep(next);
    if (next === 3) setModelingDoc('choice');
  }

  async function checkQuota(): Promise<boolean> {
    if (!profile) return false;
    const { data, error: rpcErr } = await supabase.rpc('count_user_projects_year', {
      p_user_id: profile.id,
    });
    if (rpcErr) {
      const year = new Date().getFullYear();
      const { count, error: countErr } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', profile.id)
        .gte('created_at', `${year}-01-01`)
        .lte('created_at', `${year}-12-31T23:59:59`);
      if (countErr) throw countErr;
      if ((count ?? 0) >= MAX_PROJECTS_PER_YEAR) {
        toast.error(`Limite atteinte : ${MAX_PROJECTS_PER_YEAR} projets par an maximum.`);
        return false;
      }
      return true;
    }
    if ((data as number) >= MAX_PROJECTS_PER_YEAR) {
      toast.error(`Limite atteinte : ${MAX_PROJECTS_PER_YEAR} projets par an maximum.`);
      return false;
    }
    return true;
  }

  async function generateAssistedIdeas(e: FormEvent) {
    e.preventDefault();
    if (!profile || entryKind !== 'assisted') return;
    setLoading(true);
    setPpcoDone(false);
    setIdeationId(null);
    setGenerated(null);
    try {
      const ai = await invokeAiFunction<GenerateIdeasResponse>('ai-ideation', {
        domain: contextForm.domain,
        country_code: contextForm.country_code,
        city: contextForm.city,
        target_audience: contextForm.target_audience,
        investment_capital: contextForm.investment_capital,
      });
      const ideas = Array.isArray(ai.ideas) ? ai.ideas : [];
      if (ideas.length < 3) {
        throw new Error('Au moins 3 idées sont attendues. Réessayez la génération.');
      }
      setSuggestions(ideas);
      toast.success(
        `${ideas.length} idées proposées — choisissez celle qui vous intéresse (seule elle sera enregistrée).`,
        'Idéation',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Génération impossible');
    } finally {
      setLoading(false);
    }
  }

  async function selectSuggestedIdea(idea: SuggestedIdea) {
    if (!profile || entryKind !== 'assisted') return;
    setLoading(true);
    try {
      const { data, error: insertError } = await supabase
        .from('ideations')
        .insert({
          user_id: profile.id,
          domain: contextForm.domain,
          country_code: contextForm.country_code,
          city: contextForm.city,
          target_audience: idea.target_customers || contextForm.target_audience,
          business_idea: idea.business_idea,
          problems: idea.problems?.length ? idea.problems : ['À préciser'],
          solutions: idea.solutions?.length ? idea.solutions : ['À préciser'],
          entry_kind: 'assisted',
          ppco_skipped: false,
          investment_capital: contextForm.investment_capital.trim() || null,
          status: 'generated',
        })
        .select('id')
        .single();
      if (insertError) throw insertError;
      setIdeationId(data.id);
      setGenerated(idea);
      setCurrentIdeaTitle(idea.business_idea);
      setSuggestions([]);
      setIdeationModalOpen(false);
      toast.success(
        `Idée enregistrée (statut : ${ideationStatusLabel('generated')}) — validez-la via PPCO.`,
        'Idéation',
      );
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveManualIdea(e: FormEvent) {
    e.preventDefault();
    if (!profile || !entryKind || entryKind === 'assisted') return;
    setLoading(true);
    setPpcoDone(false);
    try {
      const skipPpco = entryKind === 'existing_activity';
      if (skipPpco) {
        const okQuota = await checkQuota();
        if (!okQuota) {
          setLoading(false);
          return;
        }
      }
      const problems = manualIdea.problems
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const solutions = manualIdea.solutions
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const { data, error: insertError } = await supabase
        .from('ideations')
        .insert({
          user_id: profile.id,
          domain: contextForm.domain,
          country_code: contextForm.country_code,
          city: contextForm.city,
          target_audience: contextForm.target_audience,
          business_idea: manualIdea.business_idea.trim(),
          problems: problems.length ? problems : ['À préciser'],
          solutions: solutions.length ? solutions : ['À préciser'],
          entry_kind: entryKind,
          ppco_skipped: skipPpco,
          activity_notes: manualIdea.activity_notes.trim() || null,
          status: skipPpco ? 'retained' : 'generated',
        })
        .select('id')
        .single();
      if (insertError) throw insertError;
      setIdeationId(data.id);
      setCurrentIdeaTitle(manualIdea.business_idea.trim());
      setGenerated({
        business_idea: manualIdea.business_idea.trim(),
        problems: problems.length ? problems : ['À préciser'],
        solutions: solutions.length ? solutions : ['À préciser'],
      });
      setIdeationModalOpen(false);
      if (skipPpco) {
        toast.success(
          'Activité enregistrée comme projet « en cours » — passez au diagnostic IGPE.',
          'Idéation',
        );
        setRefreshKey((k) => k + 1);
        setStep(2);
      } else {
        toast.success(
          `Idée enregistrée (statut : ${ideationStatusLabel('generated')}) — validez-la via PPCO.`,
          'Idéation',
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement impossible');
    } finally {
      setLoading(false);
    }
  }

  async function retainIdea(decision: 'retained' | 'rejected') {
    if (!ideationId) return;
    if (decision === 'retained' && requiresPpco && !ppcoDone) {
      toast.error('Complétez d’abord la validation PPCO.');
      return;
    }
    if (decision === 'retained') {
      const okQuota = await checkQuota();
      if (!okQuota) return;
    }
    const { error: upErr } = await supabase.from('ideations').update({ status: decision }).eq('id', ideationId);
    if (upErr) {
      toast.error(upErr.message);
      return;
    }
    setRefreshKey((k) => k + 1);
    if (decision === 'retained') {
      toast.success(`Idée retenue et convertie en projet — passez à l’étape ${step2Label}.`, 'Idéation');
      setStep(2);
    } else {
      toast.info('Idée entrepreneuriale rejetée (statut : rejeté). Vous pouvez tenter une autre idée.', 'Idéation');
      resetIdeaWork();
    }
  }

  function tryAnotherIdea() {
    resetIdeaWork();
    setIdeationModalOpen(true);
    toast.info(
      entryKind === 'assisted'
        ? 'Nouvelle tentative — régénérez des suggestions ou choisissez un autre profil.'
        : 'Nouvelle tentative — saisissez une autre idée.',
      'Idéation',
    );
  }

  async function resumeIdeation(idea: Ideation) {
    setEntryKind(idea.entry_kind ?? 'assisted');
    setIdeationId(idea.id);
    setCurrentIdeaTitle(idea.business_idea);
    setSuggestions([]);
    setGenerated({
      business_idea: idea.business_idea,
      problems: idea.problems ?? [],
      solutions: idea.solutions ?? [],
    });
    setContextForm({
      domain: idea.domain,
      country_code: idea.country_code,
      city: idea.city,
      target_audience: idea.target_audience,
      investment_capital: idea.investment_capital ?? '',
    });
    setPpcoDone(Boolean(idea.ppco_evaluation) || idea.ppco_skipped);
    setIdeationModalOpen(false);
    setStep2Done(false);
    setModelingDoc('choice');

    const [{ data: bmcRow }, { data: bpRow }] = await Promise.all([
      supabase.from('business_model_canvases').select('*').eq('ideation_id', idea.id).maybeSingle(),
      supabase.from('business_plans').select('*').eq('ideation_id', idea.id).maybeSingle(),
    ]);
    if (bmcRow) {
      setBmc({
        key_partners: bmcRow.key_partners ?? '',
        key_activities: bmcRow.key_activities ?? '',
        key_resources: bmcRow.key_resources ?? '',
        value_propositions: bmcRow.value_propositions ?? '',
        customer_relationships: bmcRow.customer_relationships ?? '',
        channels: bmcRow.channels ?? '',
        customer_segments: bmcRow.customer_segments ?? '',
        cost_structure: bmcRow.cost_structure ?? '',
        revenue_streams: bmcRow.revenue_streams ?? '',
      });
    } else {
      setBmc(emptyBmc);
    }
    if (bpRow?.content && typeof bpRow.content === 'object') {
      setBpContent({ ...emptyBusinessPlanContent(), ...(bpRow.content as Record<string, string>) });
    } else {
      setBpContent(emptyBusinessPlanContent());
    }

    toast.info(`Reprise de « ${idea.business_idea} ».`, 'Conception');
    setStep(2);
  }

  async function saveBmc() {
    if (!profile || !ideationId) return;
    setLoading(true);
    try {
      const bmcComplete = BMC_BLOCKS.every((b) => (bmc[b.key] ?? '').trim().length > 0);
      const { error: bmcErr } = await supabase.from('business_model_canvases').upsert(
        {
          user_id: profile.id,
          ideation_id: ideationId,
          key_partners: bmc.key_partners,
          key_activities: bmc.key_activities,
          key_resources: bmc.key_resources,
          value_propositions: bmc.value_propositions,
          customer_relationships: bmc.customer_relationships,
          channels: bmc.channels,
          customer_segments: bmc.customer_segments,
          cost_structure: bmc.cost_structure,
          revenue_streams: bmc.revenue_streams,
          is_complete: bmcComplete,
        },
        { onConflict: 'ideation_id' },
      );
      if (bmcErr) throw bmcErr;
      setRefreshKey((k) => k + 1);
      toast.success(
        bmcComplete
          ? 'Business Model Canvas complet — vous pouvez ensuite rédiger le Business Plan.'
          : 'Brouillon BMC enregistré. Complétez tous les blocs pour le finaliser.',
        'Modélisation',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement BMC impossible');
    } finally {
      setLoading(false);
    }
  }

  async function saveBp() {
    if (!profile || !ideationId) return;
    setLoading(true);
    try {
      const bpComplete = BUSINESS_PLAN_FIELDS.every((f) => (bpContent[f.key] ?? '').trim().length > 0);
      const { error: bpErr } = await supabase.from('business_plans').upsert(
        {
          user_id: profile.id,
          ideation_id: ideationId,
          content: bpContent,
          is_complete: bpComplete,
        },
        { onConflict: 'ideation_id' },
      );
      if (bpErr) throw bpErr;
      setRefreshKey((k) => k + 1);
      toast.success(
        bpComplete
          ? 'Business Plan complet — documents prêts au téléchargement dans la synthèse.'
          : 'Brouillon Business Plan enregistré. Complétez tous les champs pour le finaliser.',
        'Modélisation',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement Business Plan impossible');
    } finally {
      setLoading(false);
    }
  }

  const ideationForm = entryKind ? (
    <>
      <h4 style={{ marginTop: 0 }}>Contexte</h4>
      <div className="grid-2">
        <div className="field">
          <label>Domaine d’activité</label>
          <input
            required
            value={contextForm.domain}
            onChange={(e) => setContextForm({ ...contextForm, domain: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Pays (ISO)</label>
          <input
            required
            maxLength={2}
            value={contextForm.country_code}
            onChange={(e) =>
              setContextForm({ ...contextForm, country_code: e.target.value.toUpperCase() })
            }
          />
        </div>
        <div className="field">
          <label>Ville / zone d’opération</label>
          <input
            required
            value={contextForm.city}
            onChange={(e) => setContextForm({ ...contextForm, city: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Clients cibles</label>
          <input
            required
            value={contextForm.target_audience}
            onChange={(e) => setContextForm({ ...contextForm, target_audience: e.target.value })}
          />
        </div>
        {entryKind === 'assisted' ? (
          <div className="field">
            <label>Capital d’investissement possible</label>
            <input
              value={contextForm.investment_capital}
              onChange={(e) =>
                setContextForm({ ...contextForm, investment_capital: e.target.value })
              }
              placeholder="Ex. 2 000 000 FCFA"
            />
          </div>
        ) : null}
      </div>

      {entryKind === 'assisted' ? (
        <form onSubmit={(e) => void generateAssistedIdeas(e)}>
          <button className="btn btn-accent" type="submit" disabled={loading}>
            {loading ? 'Génération…' : 'Proposer au moins 3 idées'}
          </button>
        </form>
      ) : (
        <form onSubmit={(e) => void saveManualIdea(e)}>
          <div className="field">
            <label>{isExisting ? 'Intitulé de l’activité / projet' : 'Intitulé de l’idée'}</label>
            <textarea
              required
              value={manualIdea.business_idea}
              onChange={(e) => setManualIdea({ ...manualIdea, business_idea: e.target.value })}
            />
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Besoin ou problème identifié (un par ligne)</label>
              <textarea
                value={manualIdea.problems}
                onChange={(e) => setManualIdea({ ...manualIdea, problems: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Solution entrepreneuriale proposée (une par ligne)</label>
              <textarea
                value={manualIdea.solutions}
                onChange={(e) => setManualIdea({ ...manualIdea, solutions: e.target.value })}
              />
            </div>
          </div>
          {isExisting ? (
            <div className="field">
              <label>Notes sur l’activité (optionnel)</label>
              <textarea
                value={manualIdea.activity_notes}
                onChange={(e) => setManualIdea({ ...manualIdea, activity_notes: e.target.value })}
              />
            </div>
          ) : null}
          <button className="btn btn-accent" type="submit" disabled={loading}>
            {loading
              ? 'Enregistrement…'
              : isExisting
                ? 'Enregistrer comme projet et passer au diagnostic'
                : 'Enregistrer l’idée'}
          </button>
        </form>
      )}

      {suggestions.length > 0 && !ideationId ? (
        <div className="u-idea-suggestions">
          <h4>Idées proposées — choisissez-en une</h4>
          <div className="u-idea-suggestions-grid">
            {suggestions.map((idea, idx) => (
              <div
                key={`${idea.business_idea}-${idx}`}
                className={`u-idea-suggestion${loading ? ' is-disabled' : ''}`}
                role="button"
                tabIndex={0}
                aria-disabled={loading}
                onClick={() => {
                  if (loading) return;
                  void selectSuggestedIdea(idea);
                }}
                onKeyDown={(e) => {
                  if (loading) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    void selectSuggestedIdea(idea);
                  }
                }}
              >
                <div className="u-idea-suggestion-top">
                  <span className="u-choice-step">{String(idx + 1).padStart(2, '0')}</span>
                  <span className="u-choice-icon blue" aria-hidden>
                    {idx + 1}
                  </span>
                </div>
                <h5>{idea.business_idea}</h5>
                <p className="u-idea-suggestion-meta">
                  Zone : {idea.zone || contextForm.city} · Clients :{' '}
                  {idea.target_customers || contextForm.target_audience}
                </p>
                <div className="u-idea-suggestion-cols">
                  <div>
                    <p className="u-idea-result-label">Besoins</p>
                    <ul>
                      {(idea.problems ?? []).slice(0, 3).map((p) => (
                        <li key={p}>{p}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="u-idea-result-label">Solution</p>
                    <ul>
                      {(idea.solutions ?? []).slice(0, 3).map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ul>
                  </div>
                </div>
                <span className="u-choice-cta" style={{ color: '#1d4ed8' }}>
                  Choisir cette idée
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </>
  ) : null;

  return (
    <div className="u-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">
            Étape {step}/3 — {STEP_LABELS[step]}
            {step === 2 ? ` (${step2Label})` : ''}
          </p>
        </div>
      </div>

      <ConceptionProcessSteps step={step} step2Detail={step2Label} onSelect={goToStep} />

      <ConceptionSynthesis
        activeIdeationId={ideationId}
        refreshKey={refreshKey}
        onSelect={(idea) => void resumeIdeation(idea)}
      />

      {step === 1 ? (
        <section className="u-card u-ideation-section">
          <div className="u-section-head">
            <h3 className="u-section-title" style={{ marginTop: 0 }}>
              Idéation
            </h3>
            <p className="u-section-lead">
              Choisissez votre situation de départ. Le formulaire s’ouvrira en fenêtre.
            </p>
          </div>

          <div className="u-choice-grid">
            {IDEATION_ENTRY_KINDS.map((k) => {
              const meta = ENTRY_CARD_META[k.key];
              return (
                <button
                  key={k.key}
                  type="button"
                  className={`u-choice u-choice--${meta.tone}${entryKind === k.key ? ' is-selected' : ''}`}
                  onClick={() => chooseEntryKind(k.key)}
                >
                  <div className="u-choice-top">
                    <span className="u-choice-step">{meta.step}</span>
                    <span className={`u-choice-icon ${meta.tone}`} aria-hidden>
                      {meta.icon}
                    </span>
                  </div>
                  <h4>{k.title}</h4>
                  <p>{k.description}</p>
                  <div className="u-choice-foot">
                    <span className="u-choice-badge">Ensuite : {k.nextLabel}</span>
                    <span className="u-choice-cta">{meta.cta}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {generated && ideationId ? (
            <div className="u-idea-result">
              <div className="u-idea-result-head">
                <span className="u-idea-result-badge">Idée en cours</span>
                <span className="chip">{ideationStatusLabel(ppcoDone ? 'ppco_done' : 'generated')}</span>
              </div>
              <h4>{currentIdeaTitle || generated.business_idea}</h4>
              <div className="u-idea-result-cols">
                <div>
                  <p className="u-idea-result-label">Problèmes</p>
                  <ul>
                    {generated.problems.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="u-idea-result-label">Solutions</p>
                  <ul>
                    {generated.solutions.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {!isExisting ? (
                <>
                  <ScamperPanel ideationId={ideationId} businessIdea={generated.business_idea} />
                  <PpcoPanel
                    ideationId={ideationId}
                    businessIdea={generated.business_idea}
                    onCompleted={() => setPpcoDone(true)}
                  />
                  <div className="u-actions" style={{ marginTop: '1rem' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!ppcoDone}
                      onClick={() => void retainIdea('retained')}
                    >
                      Retenir l’idée (devenir projet)
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => void retainIdea('rejected')}>
                      Rejeter
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={tryAnotherIdea}>
                      Tenter une autre idée
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {step === 2 ? (
        <section className="u-card">
          <h3>{step2Label}</h3>
          {!ideationId ? (
            <p className="muted">Retenez d’abord une idée à l’étape Idéation.</p>
          ) : isExisting ? (
            <>
              <DiagnosticWizard
                ideationId={ideationId}
                businessIdea={currentIdeaTitle || generated?.business_idea || ''}
                meta={{
                  title: currentIdeaTitle || generated?.business_idea || '',
                  domain: contextForm.domain,
                  zone: [contextForm.city, contextForm.country_code].filter(Boolean).join(', '),
                  problems: (generated?.problems ?? []).join(' · ') || '—',
                  solution: (generated?.solutions ?? []).join(' · ') || currentIdeaTitle || '',
                  porter: profile ? `${profile.first_name} ${profile.last_name}`.trim() : '',
                }}
                onDone={() => {
                  setRefreshKey((k) => k + 1);
                  setStep2Done(true);
                  toast.success(
                    'Diagnostic terminé — téléchargez le rapport PDF, puis passez à la modélisation.',
                    'Diagnostic',
                  );
                }}
              />
              {step2Done ? (
                <div className="conception-next-step">
                  <p>Étape suivante : Modélisation (Business Model Canvas, puis Business Plan).</p>
                  <button type="button" className="btn btn-primary" onClick={() => goToStep(3)}>
                    Étape suivante → Modélisation
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <FeasibilityWizard
                ideationId={ideationId}
                meta={{
                  title: currentIdeaTitle || generated?.business_idea || '',
                  domain: contextForm.domain,
                  zone: [contextForm.city, contextForm.country_code].filter(Boolean).join(', '),
                }}
                onDone={() => {
                  setRefreshKey((k) => k + 1);
                  setStep2Done(true);
                  toast.success('Faisabilité terminée — passez à la modélisation.', 'Faisabilité');
                }}
              />
              {step2Done ? (
                <div className="conception-next-step">
                  <p>Étape suivante : Modélisation (Business Model Canvas, puis Business Plan).</p>
                  <button type="button" className="btn btn-primary" onClick={() => goToStep(3)}>
                    Étape suivante → Modélisation
                  </button>
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {step === 3 ? (
        <section className="u-card">
          <h3 style={{ marginTop: 0 }}>Modélisation</h3>
          {!ideationId ? (
            <p className="muted">Sélectionnez ou retenez une idée avant la modélisation.</p>
          ) : (
            <>
              <p className="muted">Idée : {currentIdeaTitle || generated?.business_idea || ideationId}</p>

              {modelingDoc === 'choice' ? (
                <>
                  <p>
                    Commencez par le <strong>Business Model Canvas</strong>, puis rédigez le{' '}
                    <strong>Business Plan</strong>.
                  </p>
                  <div className="conception-model-choice">
                    <button
                      type="button"
                      className="conception-model-card"
                      onClick={() => setModelingDoc('bmc')}
                    >
                      <h4>Business Model Canvas</h4>
                      <p>
                        Structurez votre modèle économique en 9 blocs (partenaires, activités,
                        proposition de valeur, segments, etc.).
                      </p>
                    </button>
                    <button
                      type="button"
                      className="conception-model-card"
                      onClick={() => setModelingDoc('bp')}
                    >
                      <h4>Business Plan</h4>
                      <p>
                        Détaillez le plan d’affaires après le BMC : marché, offre, organisation,
                        financier et déploiement.
                      </p>
                    </button>
                  </div>
                </>
              ) : null}

              {modelingDoc === 'bmc' ? (
                <>
                  <div className="u-card-head" style={{ marginBottom: '1rem' }}>
                    <button type="button" className="btn btn-ghost" onClick={() => setModelingDoc('choice')}>
                      ← Retour au choix
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => setModelingDoc('bp')}>
                      Continuer vers le Business Plan →
                    </button>
                  </div>
                  <h4>Business Model Canvas</h4>
                  <div className="grid-2">
                    {BMC_BLOCKS.map((b) => (
                      <div className="field" key={b.key}>
                        <label>{b.label}</label>
                        <textarea
                          value={bmc[b.key]}
                          onChange={(e) => setBmc({ ...bmc, [b.key]: e.target.value })}
                        />
                      </div>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={loading || !ideationId}
                      onClick={() => void saveBmc()}
                    >
                      Enregistrer le Business Model Canvas
                    </button>
                    <button type="button" className="btn btn-accent" onClick={() => setModelingDoc('bp')}>
                      Passer au Business Plan
                    </button>
                  </div>
                </>
              ) : null}

              {modelingDoc === 'bp' ? (
                <>
                  <div className="u-card-head" style={{ marginBottom: '1rem' }}>
                    <button type="button" className="btn btn-ghost" onClick={() => setModelingDoc('choice')}>
                      ← Retour au choix
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => setModelingDoc('bmc')}>
                      ← Revenir au Business Model Canvas
                    </button>
                  </div>
                  <h4>Business Plan</h4>
                  {bpSections.map(([section, fields]) => (
                    <div key={section}>
                      <h4>{section}</h4>
                      {fields.map((f) => (
                        <div className="field" key={f.key}>
                          <label>{f.label}</label>
                          <textarea
                            value={bpContent[f.key] ?? ''}
                            onChange={(e) => setBpContent({ ...bpContent, [f.key]: e.target.value })}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={loading || !ideationId}
                    onClick={() => void saveBp()}
                  >
                    Enregistrer le Business Plan
                  </button>
                </>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {ideationModalOpen && entryKind ? (
        <div
          className="conception-modal-backdrop"
          role="presentation"
          onClick={closeIdeationModal}
        >
          <div
            className="conception-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="conception-ideation-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="conception-modal-head">
              <div>
                <h3 id="conception-ideation-modal-title">{entryMeta?.title ?? 'Idéation'}</h3>
                <p>{entryMeta?.description}</p>
              </div>
              <button
                type="button"
                className="conception-modal-close"
                onClick={closeIdeationModal}
                aria-label="Fermer"
              >
                ×
              </button>
            </div>
            {ideationForm}
          </div>
        </div>
      ) : null}
    </div>
  );
}
